import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Search, X } from "lucide-react";
import { getSearchResults } from "../data/siteSearch";

function matchingValue(value, query) {
  return value.toLowerCase().includes(query);
}

export function SearchMenu({ searchItems, onNavigate = () => {}, mobile = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const instanceId = useId().replace(/:/g, "");
  const testPrefix = mobile ? "mobile-search-menu" : "search-menu";
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const pointerFocusRef = useRef(false);
  const menuId = `${testPrefix}-panel-${instanceId}`;
  const normalizedQuery = query.trim().toLowerCase();

  const options = useMemo(() => {
    if (!normalizedQuery) return [];
    const facetOptions = [
      ...new Set(searchItems.flatMap((item) => item.facets || [])),
    ]
      .filter((value) => matchingValue(value, normalizedQuery))
      .slice(0, 3)
      .map((value) => ({ type: "filter", label: value, detail: "Archive filter", path: `/works?search=${encodeURIComponent(value)}#search-results`, score: 700 }));
    const itemOptions = getSearchResults(searchItems, normalizedQuery)
      .slice(0, 5)
      .map(({ item, score }) => ({ type: item.type, label: item.label, detail: item.detail, path: item.path, score }));
    return [...facetOptions, ...itemOptions]
      .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
      .slice(0, 6);
  }, [normalizedQuery, searchItems]);

  useEffect(() => {
    if (!open) return undefined;
    const focusSearchInput = window.requestAnimationFrame(() => inputRef.current?.focus());
    const closeOnOutsidePointer = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      pointerFocusRef.current = true;
      triggerRef.current?.focus();
      window.setTimeout(() => {
        pointerFocusRef.current = false;
      }, 0);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.cancelAnimationFrame(focusSearchInput);
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const closeAfterNavigation = () => {
    setOpen(false);
    onNavigate();
  };

  const searchPath = normalizedQuery ? `/search?q=${encodeURIComponent(normalizedQuery)}` : "/search";

  return (
    <div
      ref={menuRef}
      className={`search-menu${open ? " is-open" : ""}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      data-testid={testPrefix}
    >
      <button
        ref={triggerRef}
        className="search-menu-trigger"
        type="button"
        aria-label="Search the site"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="dialog"
        title="Search"
        onPointerDown={() => {
          pointerFocusRef.current = true;
        }}
        onClick={() => {
          pointerFocusRef.current = false;
          setOpen((current) => !current);
        }}
        onFocus={() => {
          if (!pointerFocusRef.current) setOpen(true);
        }}
        data-testid={testPrefix + "-toggle"}
      >
        <Search size={15} strokeWidth={1.4} aria-hidden="true" />
        <span>Search</span>
      </button>
      {open && (
        <div id={menuId} className="search-menu-panel" role="dialog" aria-label="Search the public site" data-testid={testPrefix + "-panel"}>
          <form className="search-menu-form" onSubmit={(event) => { event.preventDefault(); if (!normalizedQuery) return; navigate(searchPath); closeAfterNavigation(); }} data-testid={testPrefix + "-form"}>
            <label className="sr-only" htmlFor={`${menuId}-input`}>Search the public site</label>
            <div className="search-menu-input-wrap">
              <Search size={15} strokeWidth={1.4} aria-hidden="true" />
              <input ref={inputRef} id={`${menuId}-input`} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title, series, medium, venue, or page" autoComplete="off" onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); setOpen(false); pointerFocusRef.current = true; triggerRef.current?.focus(); window.setTimeout(() => { pointerFocusRef.current = false; }, 0); } }} data-testid={testPrefix + "-input"} />
              {query && <button className="search-menu-clear" type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search" data-testid={testPrefix + "-clear-button"}><X size={14} strokeWidth={1.4} /></button>}
            </div>
            {normalizedQuery && (
              <div className="search-menu-results" data-testid={testPrefix + "-options"}>
                {options.length > 0 ? options.map((option, index) => (
                  <Link key={`${option.type}-${option.path}-${index}`} className="search-menu-option" to={option.path} onClick={closeAfterNavigation} data-testid={`${testPrefix}-option-${index}`}>
                    <span><strong>{option.label}</strong></span>
                    <ArrowUpRight size={14} strokeWidth={1.3} />
                  </Link>
                )) : <span className="search-menu-no-results" data-testid={testPrefix + "-no-results"}>No match yet. Try a title, series, medium, venue, or page name.</span>}
                <Link className="search-menu-submit" to={searchPath} onClick={closeAfterNavigation} data-testid={testPrefix + "-submit-link"}>Browse matches for “{query.trim()}” <ArrowUpRight size={14} strokeWidth={1.3} /></Link>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
